import { useLayoutEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import {
  editorUtils,
  ILayoutComponent,
  useFlowBuilder2Context,
  useEditorStore,
  LayoutComponentNames,
  LockIcon,
  PermissionEditIcon,
  useAssistantLoadingStore,
} from '@diana/core';
import { ConfigureAction } from './ConfigureAction';
import { useFindActiveTask } from '../../../Support/useFindActiveTask';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { useFlowBuilder2PageContext } from '../../../Context/useFlowBuilder2PageContext';
import { ConfigureDrawerContent } from '../ConfigureDrawer.styles';
import { ConfigureDrawerContentLoader } from '../ConfigureDrawerContentLoader';
import { TaskDrawerActionList } from '../../../Components/TaskDrawer/TaskDrawerActionList';
import { TaskDrawerConfigSwitcher } from '../../../Components/TaskDrawer/TaskDrawerConfigSwitcher';
import { DefaultEditorField } from '../../../Components/DefaultEditorField/DefaultEditorField';

export const ConfigureTaskDrawerContent = () => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { isLoading: isPageLoading } = useFlowBuilder2PageContext();
  const { updateAndSaveTaskById, updateAndSaveActionSetById } = useFlowBuilder2Context();
  const findActiveTask = useFindActiveTask();

  const [isDefaultEditorEnabled, setIsDefaultEditorEnabled] = useState<boolean>(false);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const actionIndex = useFlowBuilder2PageStore((s) => s.taskActionIndex);

  const taskId = useEditorStore((s) => findActiveTask(s.flow)?.taskId ?? '');
  const actionSetId = useEditorStore((s) => findActiveTask(s.flow)?.actionSetId ?? '');
  const isRestricted = useEditorStore((s) => !!findActiveTask(s.flow)?.isRestricted);

  const isFirstTask = useEditorStore((s) => {
    return findActiveTask(s.flow)?.taskId === s.flow?.steps[0]?.taskId;
  });

  const peopleSearchComponents = useEditorStore((s) => {
    const config = findActiveTask(s.flow);
    if (!config) {
      return [];
    }

    const layoutIds = editorUtils.getPriorLayoutIdsFromFlow(config.layoutId, s.flow);
    return layoutIds.reduce<ILayoutComponent[]>((result, layoutId) => {
      const layout = s.getLayoutById(layoutId);
      if (layout) {
        for (const component of layout.components) {
          if (component.component === LayoutComponentNames.USER_INVITATION) {
            result.push(component);
          }
        }
      }
      return result;
    }, []);
  }, isEqual);

  const disabled = isChatLoading || hasAnyPendingStatus;
  const hasPeopleComponents = peopleSearchComponents.length > 0;
  const disableDefaultEditor = isFirstTask || disabled || !hasPeopleComponents;

  const initiatorPath = useEditorStore((s) => {
    const task = findActiveTask(s.flow);
    return task ? editorUtils.getInitiatorPathFromStepConfig(task) : '';
  });

  const defaultInitiatorPath = useEditorStore((s) => {
    const task = findActiveTask(s.flow);
    return task ? editorUtils.getDefaultInitiatorPathFromStepConfig(task) : '';
  });

  useLayoutEffect(() => {
    setIsDefaultEditorEnabled(!!initiatorPath && initiatorPath !== defaultInitiatorPath);
  }, [initiatorPath, defaultInitiatorPath]);

  if (isPageLoading) {
    return (
      <ConfigureDrawerContent>
        <ConfigureDrawerContentLoader />
      </ConfigureDrawerContent>
    );
  }

  if (actionIndex >= 0) {
    return <ConfigureAction actionIndex={actionIndex} />;
  }

  return (
    <ConfigureDrawerContent gap={2}>
      <TaskDrawerActionList />
      <Stack gap={1}>
        <TaskDrawerConfigSwitcher
          title={t('labels.restrictedAccess')}
          description={t('descriptions.onlyPeopleCanSeeTaskDetails')}
          checked={isRestricted}
          disabled={disabled}
          icon={LockIcon}
          onChange={() => {
            updateAndSaveTaskById(taskId, (draft) => {
              draft.isRestricted = !isRestricted;
              return draft;
            });
          }}
        />
        <TaskDrawerConfigSwitcher
          title={t('labels.defaultEditor')}
          description={t('descriptions.allowUserEditAndSubmitTask')}
          checked={isDefaultEditorEnabled}
          disabled={disableDefaultEditor}
          icon={PermissionEditIcon}
          onChange={() => {
            setIsDefaultEditorEnabled(!isDefaultEditorEnabled);
            if (isDefaultEditorEnabled) {
              updateAndSaveActionSetById(actionSetId, (draft) => {
                editorUtils.applyInitiatorPathToStepConfig(draft, defaultInitiatorPath);
                return draft;
              });
            }
          }}
          footerContent={
            isDefaultEditorEnabled ? (
              <DefaultEditorField
                disabled={disableDefaultEditor}
                components={peopleSearchComponents}
              />
            ) : null
          }
        />
      </Stack>
    </ConfigureDrawerContent>
  );
};
